import { ChevronDown, ChevronUp, ExternalLink, Link2, X } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { SheetDescription, SheetTitle } from '#app/components/ui/sheet.tsx'
import { cn } from '#app/utils/misc.tsx'
import { type CatalogueSkill } from '#app/utils/skill-catalogue.ts'
import { formatTier, formatXp } from '#app/utils/skill-format.ts'
import {
	ANY_OF_PREREQUISITE_SKILLS,
	findSkillGroupRule,
	SKILL_OPTION_SETS,
	voieBeliefName,
} from '#app/utils/skill-groups.ts'
import { useIsDesktop } from '#app/utils/use-media-query.ts'
import { SkillDescription } from './skill-description.tsx'
import {
	CombatMark,
	DepartMark,
	Eyebrow,
	Metrics,
	TierMark,
} from './skill-marks.tsx'

export type SkillDetailContext = 'page' | 'editor' | 'sheet'

export const ghostButtonClass =
	'border-border text-muted-foreground hover:bg-muted inline-flex h-[30px] items-center gap-1.5 rounded-md border px-[11px] text-[12.5px] whitespace-nowrap transition-colors disabled:opacity-50'

export const goldButtonClass =
	'bg-primary text-primary-foreground inline-flex h-[30px] items-center rounded-md px-3.5 text-[12.5px] font-semibold whitespace-nowrap transition-opacity hover:opacity-90 disabled:opacity-50'

export function catalogueHref(skillId: string, careerId?: string | null) {
	const params = new URLSearchParams()
	if (careerId) params.set('carriere', careerId)
	params.set('skill', skillId)
	return `/competences?${params.toString()}`
}

export function SkillDetail(props: {
	skill: CatalogueSkill
	ctx?: SkillDetailContext
	onSelect: (skillId: string) => void
	onClose?: () => void
	footer?: ReactNode
}) {
	return <SkillDetailInner key={props.skill.id} {...props} />
}

// Sections fold after the lede: first open on desktop, all closed on phones.
function SkillDetailInner({
	skill,
	ctx = 'sheet',
	onSelect,
	onClose,
	footer,
}: {
	skill: CatalogueSkill
	ctx?: SkillDetailContext
	onSelect: (skillId: string) => void
	onClose?: () => void
	footer?: ReactNode
}) {
	const isDesktop = useIsDesktop()
	const [toggled, setToggled] = useState<Record<number, boolean>>({})
	const [copied, setCopied] = useState(false)
	const sections = skill.description?.sections ?? []
	const isStarting = skill.careers.some((career) => career.isStarting)
	const primaryCareer = skill.careers[0]

	return (
		<div className='flex h-full min-h-0 flex-col'>
			<div className='border-border shrink-0 border-b px-5 pt-3 pb-[13px] sm:pt-[18px]'>
				<div className='flex items-start gap-3'>
					<SheetTitle className="flex-1 font-['EB_Garamond',ui-serif,Georgia] text-2xl leading-[1.12] font-semibold tracking-[-.01em] sm:text-[26px]">
						{skill.name}
					</SheetTitle>
					<SheetDescription className='sr-only'>
						Fiche de la compétence {skill.name}
					</SheetDescription>
					{onClose ? (
						<button
							type='button'
							onClick={onClose}
							aria-label='Fermer'
							className='border-border text-muted-foreground hover:bg-muted focus-visible:ring-primary/20 grid size-[30px] shrink-0 place-items-center rounded-md border transition-colors outline-none focus-visible:ring-[3px]'
						>
							<X className='size-3.5' strokeWidth={2} />
						</button>
					) : null}
				</div>
				<div className='mt-2.5 flex flex-wrap items-center gap-2.5'>
					<Metrics
						size={13.5}
						items={[
							{ value: skill.cost, unit: 'XP' },
							skill.maxPurchase != null &&
								skill.maxPurchase > 1 && {
									value: skill.maxPurchase,
									unit: 'achats max',
								},
						]}
					/>
					<span className='bg-border h-3.5 w-px' aria-hidden='true' />
					<TierMark tier={skill.tier} big />
					{skill.typeName === 'Combat' ? <CombatMark label size={14} /> : null}
					{isStarting ? <DepartMark /> : null}
				</div>
			</div>

			<div className='min-h-0 flex-1 overflow-y-auto px-5 pt-4 pb-4 sm:pb-5'>
				{skill.description?.ledeHtml ? (
					<SkillDescription
						html={skill.description.ledeHtml}
						className='font-book text-[14.5px] leading-[1.62] sm:text-[15px]'
					/>
				) : null}

				{sections.length > 0 ? (
					<div className='border-border mt-3.5 border-t'>
						{sections.map((section, index) => {
							const open = toggled[index] ?? (isDesktop && index === 0)
							return (
								<div key={index} className='border-border border-b'>
									<button
										type='button'
										aria-expanded={open}
										onClick={() =>
											setToggled((prev) => ({ ...prev, [index]: !open }))
										}
										className='flex min-h-10 w-full items-center gap-2.5 py-[9px] text-left'
									>
										<span
											className={cn(
												'flex-1 text-[13px] font-semibold tracking-[.005em]',
												open ? 'text-foreground' : 'text-muted-foreground',
											)}
										>
											{section.heading}
										</span>
										{open ? (
											<ChevronUp className='text-muted-foreground size-3.5' />
										) : (
											<ChevronDown className='text-muted-foreground size-3.5' />
										)}
									</button>
									{open ? (
										<SkillDescription
											html={section.html}
											className='font-book pb-[13px] text-[14px] leading-[1.62] sm:text-[14.5px]'
										/>
									) : null}
								</div>
							)
						})}
					</div>
				) : null}

				<SkillMeta skill={skill} onSelect={onSelect} />
			</div>

			<div className='border-border flex shrink-0 items-center gap-2.5 border-t px-5 pt-2.5 pb-4 sm:pb-3'>
				{ctx === 'page' ? (
					<>
						<span className='text-muted-foreground flex-1 text-[11.5px]'>
							{primaryCareer?.name}
							{primaryCareer ? ' · ' : ''}
							{formatTier(skill.tier)}
						</span>
						<button
							type='button'
							className={ghostButtonClass}
							onClick={() => {
								const url = new URL(
									catalogueHref(skill.id, primaryCareer?.id),
									window.location.origin,
								)
								void navigator.clipboard?.writeText(url.toString())
								setCopied(true)
							}}
						>
							{copied ? 'Lien copié' : 'Copier le lien'}
							<Link2 className='size-3' strokeWidth={1.8} />
						</button>
					</>
				) : ctx === 'editor' && footer ? (
					footer
				) : (
					<>
						<Link
							to={catalogueHref(skill.id, primaryCareer?.id)}
							prefetch='intent'
							className={ghostButtonClass}
						>
							Voir dans le catalogue
							<ExternalLink className='size-3' strokeWidth={1.8} />
						</Link>
						<span className='flex-1' />
						<span className='text-muted-foreground text-[11.5px]'>
							{skill.careers.map((career) => career.name).join(', ')}
						</span>
					</>
				)}
			</div>
		</div>
	)
}

function SkillMeta({
	skill,
	onSelect,
}: {
	skill: CatalogueSkill
	onSelect: (skillId: string) => void
}) {
	const groupRule = findSkillGroupRule(skill.name)
	const belief = voieBeliefName(skill.name)
	const optionSet = SKILL_OPTION_SETS[skill.name]
	const anyOf = ANY_OF_PREREQUISITE_SKILLS.has(skill.name)

	return (
		<dl className='mt-4 grid grid-cols-2 gap-x-3.5 gap-y-3 sm:mt-[18px] sm:gap-x-4 sm:gap-y-[15px]'>
			<MetaRow label='Carrière' half>
				{skill.careers.length === 0
					? '—'
					: skill.careers.map((career) => career.name).join(', ')}
			</MetaRow>
			<MetaRow label='Achats max' half>
				{skill.maxPurchase ?? 'Illimité'}
				{skill.maxPurchase && skill.maxPurchase > 1
					? ` · ${formatXp(skill.cost)} par achat`
					: ''}
			</MetaRow>
			{groupRule ? (
				<MetaRow label={groupRule.label}>
					{groupRule.rule}
					{belief ? ` · ${belief}` : ''}
				</MetaRow>
			) : null}
			{optionSet ? (
				<MetaRow label={optionSet.label}>
					<ChipList>
						{optionSet.options.map((option) => (
							<SkillChip key={option.value}>{option.label}</SkillChip>
						))}
					</ChipList>
				</MetaRow>
			) : null}
			{skill.name === 'Contact Externe' ? (
				<MetaRow label='Choix'>
					Une provenance, différente de celle du personnage.
				</MetaRow>
			) : null}
			{skill.requires.length > 0 ? (
				<MetaRow
					label={
						anyOf || skill.requires.length > 1
							? 'Requiert — un parmi'
							: 'Requiert'
					}
				>
					<ChipList>
						{skill.requires.map((parent) => (
							<SkillChip
								key={parent.id}
								tone='amber'
								onClick={() => onSelect(parent.id)}
							>
								{parent.name}
							</SkillChip>
						))}
					</ChipList>
				</MetaRow>
			) : null}
			{skill.unlocks.length > 0 ? (
				<MetaRow label='Débloque'>
					<ChipList>
						{skill.unlocks.map((child) => (
							<SkillChip key={child.id} onClick={() => onSelect(child.id)}>
								{child.name}
							</SkillChip>
						))}
					</ChipList>
				</MetaRow>
			) : null}
			{skill.recipeCount > 0 ? (
				<MetaRow label='Recettes' half>
					{skill.recipeCount}
				</MetaRow>
			) : null}
			{skill.prestigeTitles.length > 0 ? (
				<MetaRow label='Titres de prestige'>
					<ChipList>
						{skill.prestigeTitles.map((title) => (
							<SkillChip key={title.id} tone='gold'>
								{title.name}
							</SkillChip>
						))}
					</ChipList>
				</MetaRow>
			) : null}
		</dl>
	)
}

function MetaRow({
	label,
	half = false,
	children,
}: {
	label: string
	half?: boolean
	children: ReactNode
}) {
	return (
		<div className={cn('min-w-0', half ? 'col-span-1' : 'col-span-2')}>
			<dt>
				<Eyebrow className='mb-1.5'>{label}</Eyebrow>
			</dt>
			<dd className='text-foreground text-[13px] tabular-nums'>{children}</dd>
		</div>
	)
}

function ChipList({ children }: { children: ReactNode }) {
	return <div className='flex flex-wrap gap-1.5'>{children}</div>
}

const chipTones = {
	neutral: 'bg-muted text-muted-foreground border-border',
	amber:
		'bg-amber-500/10 text-amber-800 border-transparent dark:text-amber-300',
	gold: 'bg-primary/10 text-primary border-primary/60',
}

export function SkillChip({
	onClick,
	tone = 'neutral',
	children,
}: {
	onClick?: () => void
	tone?: keyof typeof chipTones
	children: ReactNode
}) {
	const className = cn(
		'inline-flex h-6 items-center rounded-md border px-2 text-xs whitespace-nowrap',
		chipTones[tone],
		onClick && 'hover:border-primary/60 transition-colors',
	)
	if (!onClick) return <span className={className}>{children}</span>
	return (
		<button type='button' onClick={onClick} className={className}>
			{children}
		</button>
	)
}
